import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Typography, Grid, Modal } from 'antd';
import { PlusOutlined, InboxOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import fetchNoteTemplatesList from '../../utils/note_templates/fetchNoteTemplatesList';
import useNoteTemplatePolling from '../../hooks/useNoteTemplatePolling';
import deleteNoteTemplate from '../../utils/note_templates/deleteNoteTemplate';
import NoteTemplateNewModal from './NoteTemplateNewModal';
import NoteTemplateUpdateModal from './NoteTemplateUpdateModal';
import NoteTemplatesList from './NoteTemplatesList';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const NoteTemplates = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewModalVisible, setIsNewModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [processingTemplate, setProcessingTemplate] = useState(null);
  const [error, setError] = useState(null);
  
  // Polling state for file processing
  const [pollingUuid, setPollingUuid] = useState(null);
  const { template: polledTemplate, loading: isPolling, error: pollingError } = useNoteTemplatePolling(pollingUuid);

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Handle polling completion
  useEffect(() => {
    if (polledTemplate && polledTemplate.isReady()) {
      // Template is ready, show the update modal and close the new modal
      setProcessingTemplate(polledTemplate);
      setIsUpdateModalVisible(true);
      setIsNewModalVisible(false); // Close the new modal
      setPollingUuid(null); // Stop polling
    }
  }, [polledTemplate]);

  // Handle polling errors
  useEffect(() => {
    if (pollingError) {
      toast.error(t('note_templates.errors.polling_timeout'));
      setPollingUuid(null); // Stop polling
    }
  }, [pollingError]);

  const fetchTemplates = async (page = 1, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchNoteTemplatesList(page, params);
      setTemplates(result.templates);
      // Note: pagination is available in result.pagination if needed later
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
      setError('Failed to load templates');
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

    const handleTemplateCreated = (presenter) => {
    // Start polling until template is ready
    setPollingUuid(presenter.getUUID());
  };

  const handleTemplateUpdated = (updatedTemplate) => {
    // Template has been updated, refresh the list and close modal
    setIsUpdateModalVisible(false);
    setProcessingTemplate(null);
    setEditingTemplate(null);
    fetchTemplates();
  };



  const handleDeleteTemplate = async (templateUuid) => {
    Modal.confirm({
      title: t('note_templates.delete_confirm_title'),
      content: t('note_templates.delete_confirm_content'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteNoteTemplate(templateUuid);
          toast.success(t('note_templates.delete_success'));
          fetchTemplates();
        } catch (error) {
          toast.error(t('note_templates.errors.delete'));
        }
      },
    });
  };

  const showCreateModal = () => {
    setIsNewModalVisible(true);
  };

  const showEditModal = (template) => {
    setEditingTemplate(template);
    setIsUpdateModalVisible(true);
  };





  return (
    <div>
      <Paragraph style={{ display: "block" }}>{t('note_templates.subtitle')}</Paragraph>
      <Paragraph style={{ display: "block" }}>{t('note_templates.note')}</Paragraph>

      {error && (
        <div data-testid="error-message" style={{ marginBottom: "16px" }}>
          <Text type="danger">{error}</Text>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "26px", marginTop: "25px" }}>
        <Button
          data-testid="add-template-button"
          type="primary"
          icon={<PlusOutlined />}
          onClick={showCreateModal}
        >
          {t('note_templates.create')}
        </Button>
      </div>

      {loading ? (
        <Card loading />
      ) : templates.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <InboxOutlined style={{ fontSize: "48px", color: "#bfbfbf", marginBottom: "16px", display: "block" }} />
            <Text type="secondary">{t('note_templates.empty_state')}</Text>
          </div>
        </Card>
      ) : (
        <NoteTemplatesList
          templates={templates}
          onEdit={showEditModal}
          onDelete={handleDeleteTemplate}
        />
      )}

            {/* New Template Modal */}
      <NoteTemplateNewModal
        open={isNewModalVisible}
        onCancel={() => setIsNewModalVisible(false)}
        onTemplateCreated={handleTemplateCreated}
      />

      {/* Update Template Modal - Used for both new templates and editing existing ones */}
      <NoteTemplateUpdateModal
        open={isUpdateModalVisible}
        onCancel={() => {
          setIsUpdateModalVisible(false);
          setProcessingTemplate(null);
          setEditingTemplate(null);
        }}
        onSave={handleTemplateUpdated}
        template={editingTemplate || processingTemplate}
      />




    </div>
  );
};

export default NoteTemplates;
